import { useState, useRef } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import usePageSEO from "@/hooks/usePageSEO";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Helmet } from "react-helmet";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { useConsentRequired } from "@/hooks/useConsentRequired";
import { useTurnstile } from "@/hooks/useTurnstile";


const Contact = () => {
  usePageSEO({
    title: "Contact | Bio Beauté Zen",
    description: "Contactez Bio Beauté Zen à Montpellier. Prenez rendez-vous par téléphone, email ou via mon formulaire. Horaires et plan d'accès disponibles.",
    canonical: "https://biobeautezen.com/contact-institut-montpellier",
    ogTitle: "Contact | Bio Beauté Zen Montpellier",
    ogDescription: "Contactez Bio Beauté Zen à Montpellier. Prenez rendez-vous par téléphone, email ou via mon formulaire."
  });

  const { trackEvent } = useGoogleAnalytics();

  // Demander le consentement des cookies pour cette page
  useConsentRequired();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    consent: false,
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Piège anti-bot : champ invisible qui doit rester vide
  const [honeypot, setHoneypot] = useState("");
  // Horodatage d'ouverture du formulaire (un bot répond en moins d'une seconde)
  const formOpenedAt = useRef<number>(Date.now());
  // CAPTCHA invisible (Cloudflare Turnstile)
  const turnstile = useTurnstile();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;

    const { name, value, type, checked } = target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value, // ✅ checkbox -> checked, le reste -> value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ Garde de sécurité côté frontend
    if (!formData.consent) {
      toast({
        title: "Consentement nécessaire",
        description: "Merci de cocher la case de consentement avant d'envoyer votre message.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Token du CAPTCHA invisible (null si désactivé ou indisponible : on n'empêche jamais l'envoi)
      const turnstileToken = await turnstile.getToken();

      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          type: "contact",
          data: {
            ...formData,
            honeypot,
            elapsed_ms: Date.now() - formOpenedAt.current,
            turnstile_token: turnstileToken ?? undefined,
          },
        },
      });
      if (error) throw error;
      turnstile.reset();
      setFormSubmitted(true);
      toast({
        title: "Message envoyé",
        description: "Votre message a bien été envoyé. Nous vous répondrons dans les plus brefs délais.",
      });


      trackEvent("contact_form_submit", {
        subject: formData.subject,
      });
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi du message. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Accueil",
                item: "https://biobeautezen.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Contact",
                item: "https://biobeautezen.com/contact-institut-montpellier",
              },
            ],
          })}
        </script>
      </Helmet>
      <div className="pt-20 pb-4">
        <div className="container mx-auto px-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Contact</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
      <div className="pb-16">
        {/* Hero Section */}
        <section className="bg-bbz-beige/40 py-16 md:py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl md:text-5xl font-semibold text-bbz-text mb-6">Contact</h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto">
              Je reste à votre écoute pour toute question ou pour prendre rendez-vous
            </p>
          </div>
        </section>

        {/* Coordonnées et carte */}
        <section className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="section-title">Contact</h2>

              <div className="space-y-8 mb-10">
                <div className="flex items-start">
                  <div className="bg-bbz-green/30 p-3 rounded-full mr-4">
                    <MapPin className="w-6 h-6 text-bbz-text" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-xl font-medium mb-2">Adresse</h3>
                    <p className="mb-1">1420 Chem. de Moulares,</p>
                    <p className="mb-2">34070, Montpellier, France</p>
                    <p className="text-sm text-bbz-text/70 flex items-center gap-1">
                      <span className="text-bbz-green">•</span>
                      En face de la pharmacie (angle rue de Centrayrargues)
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-bbz-green/30 p-3 rounded-full mr-4">
                    <Phone className="w-6 h-6 text-bbz-text" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-xl font-medium mb-2">Téléphone</h3>
                    <p>
                      <a href="tel:0499538308" className="hover:text-bbz-green">
                        04 99 53 83 08
                      </a>
                    </p>
                    <p>(laissez un message sur le répondeur)</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-bbz-green/30 p-3 rounded-full mr-4">
                    <Mail className="w-6 h-6 text-bbz-text" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-xl font-medium mb-2">Email</h3>
                    <p>
                      <a
                        href="mailto:biobeautezen.institut@gmail.com?subject=Demande%20d'information%20-%20Bio%20Beaut%C3%A9%20Zen"
                        className="hover:text-bbz-green"
                      >
                        biobeautezen.institut@gmail.com
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-bbz-green/30 p-3 rounded-full mr-4">
                    <Clock className="w-6 h-6 text-bbz-text" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-xl font-medium mb-2">Horaires d'ouverture</h3>
                    <ul className="space-y-1">
                      <li>
                        <span className="font-medium">Mardi - Jeudi - Vendredi:</span> 9h30 - 18h30
                      </li>
                      <li>
                        <span className="font-medium">Samedi:</span> 9h30 - 13h30
                      </li>
                      <li>
                        <span className="font-medium">Lundi - Mercredi - Dimanche :</span> Fermé
                      </li>
                    </ul>
                    <p className="mt-3 text-sm text-bbz-text/70 italic">
                      Vérifiez auprès de l'institut les horaires d'ouverture actuels.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-bbz-pink/20 p-6 rounded-lg">
                <h3 className="font-playfair text-xl font-medium mb-3">Prendre rendez-vous</h3>
                <p className="mb-4">Pour réserver un soin, cliquez ci-dessous :</p>
                <a
                  href="https://www.kalendes.com/site/biobeautezen/welcome#%2Freserve"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-block"
                >
                  Réserver en ligne
                </a>
              </div>
            </div>

            {/* Carte Google Maps */}
            <div>
              <h2 className="section-title">Trouver l'institut</h2>
              <div className="rounded-lg overflow-hidden h-[400px] shadow-md">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5779.203182659116!2d3.8873560762965313!3d43.59401425625664!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b6af9469c484e7%3A0xb077f1c9cad6612!2sInstitut%20Bio%20Beaut%C3%A9%20Zen%20%C3%A0%20Montpellier!5e0!3m2!1sfr!2sfr!4v1746728781334!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{
                    border: 0,
                  }}
                  allowFullScreen={false}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps"
                ></iframe>
              </div>
              <div className="mt-4">
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Institut+Bio+Beauté+Zen,1420+Chem.+de+Moulares,34070+Montpellier"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2 w-full justify-center"
                >
                  <MapPin className="w-5 h-5" />
                  Obtenir l'itinéraire
                </a>
              </div>

              {/* Note de localisation */}
              <div className="mt-4 p-4 bg-bbz-beige/30 rounded-lg border border-bbz-green/20">
                <p className="text-sm text-bbz-text/80 leading-relaxed">
                  <span className="font-medium text-bbz-green">📍 Repères :</span> L'institut se situe dans le sud de
                  Montpellier, entre le rond-point des Près d'Arènes Garcia Lorca et Hôtel de Ville Port Marianne, en
                  face de la pharmacie qui fait angle avec la rue de Centrayrargues et le chemin de Moularès.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Formulaire de contact */}
        <section className="bg-bbz-green/20 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="section-title text-center mx-auto after:mx-auto">Contactez-moi</h2>
              <p className="text-center mb-8">
                Une question, une demande spécifique ? Complétez ce formulaire et je vous répondrai dans les meilleurs
                délais.
              </p>

              {formSubmitted ? (
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                  <div className="w-16 h-16 bg-bbz-green/30 rounded-full mx-auto flex items-center justify-center mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-playfair text-xl font-medium mb-3">Message envoyé !</h3>
                  <p className="mb-6">
                    Je vous remercie de m'avoir contactée. Votre message a bien été reçu et je vous répondrai dans les
                    plus brefs délais.
                  </p>
                  <button onClick={() => setFormSubmitted(false)} className="btn-secondary">
                    Envoyer un nouveau message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-bbz-text font-medium mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full p-3 border border-bbz-beige rounded-md focus:outline-none focus:ring-2 focus:ring-bbz-green"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-bbz-text font-medium mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full p-3 border border-bbz-beige rounded-md focus:outline-none focus:ring-2 focus:ring-bbz-green"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="phone" className="block text-bbz-text font-medium mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full p-3 border border-bbz-beige rounded-md focus:outline-none focus:ring-2 focus:ring-bbz-green"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-bbz-text font-medium mb-2">
                        Sujet *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full p-3 border border-bbz-beige rounded-md focus:outline-none focus:ring-2 focus:ring-bbz-green"
                      >
                        <option value="">Sélectionnez un sujet</option>
                        <option value="information">Demande d'information</option>
                        <option value="rendez-vous">Prise de rendez-vous</option>
                        <option value="bon-cadeau">Bon cadeau</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-bbz-text font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full p-3 border border-bbz-beige rounded-md focus:outline-none focus:ring-2 focus:ring-bbz-green"
                    ></textarea>
                  </div>

                  {/* Champ piège anti-robots : invisible pour les visiteurs */}
                  <div aria-hidden="true" className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
                    <label htmlFor="website">Site web</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>

                  {/* CAPTCHA invisible : ne s'affiche que si un défi est nécessaire */}
                  <div ref={turnstile.containerRef} />


                  <div className="mb-6">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        id="consent"
                        name="consent"
                        checked={formData.consent}
                        onChange={handleChange}
                        required
                        className="mt-1 mr-2"
                      />
                      <span className="text-sm">
                        J'accepte que mes données soient utilisées uniquement dans le cadre de ma demande et de la
                        relation commerciale éthique et personnalisée qui pourrait en découler. *
                      </span>
                    </label>
                  </div>

                  <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                    {isSubmitting ? "Envoi en cours..." : "Envoyer"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Call to action FAQ */}
        <section className="section-container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-bbz-green/20 to-bbz-pink/20 p-8 md:p-12 rounded-2xl text-center">
              <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-bbz-text mb-4">Des questions ?</h2>
              <p className="text-lg mb-6 max-w-2xl mx-auto">
                Consultez ma Foire aux Questions pour trouver les réponses aux questions les plus courantes sur mes
                soins, mes produits et mon institut.
              </p>
              <Link to="/questions-frequentes-institut-beaute" className="btn-primary inline-block">
                Consulter la FAQ
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
